import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { checkPurchaseStatus } from '../../services/creditService';
import { CheckCircle, XCircle } from 'lucide-react';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { Link } from 'react-router-dom';

const POLLING_INTERVAL = 2000; // 2 secondes
const MAX_POLLING_ATTEMPTS = 30; // 1 minute maximum

const PaymentConfirmation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [pollingCount, setPollingCount] = useState(0);

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        console.log('Vérification du paiement...');
        if (!user) {
          console.log('Utilisateur non connecté, redirection vers login');
          navigate('/login');
          return;
        }

        const sessionId = searchParams.get('session_id');
        console.log('Session ID:', sessionId);
        if (!sessionId) {
          setError('Session de paiement invalide');
          setLoading(false);
          return;
        }

        const status = await checkPurchaseStatus(sessionId);
        console.log('Statut du paiement:', status);

        if (status === 'completed') {
          console.log('Paiement confirmé avec succès');
          setSuccess(true);
          setLoading(false);
          setTimeout(() => navigate('/profile'), 3000);
        } else if (status === 'failed') {
          console.log('Paiement échoué');
          setError('Le paiement a échoué');
          setLoading(false);
        } else if (pollingCount < MAX_POLLING_ATTEMPTS) {
          console.log(`Attente de confirmation... (${pollingCount + 1}/${MAX_POLLING_ATTEMPTS})`);
          // Continuer le polling
          setPollingCount(prev => prev + 1);
          setTimeout(verifyPayment, POLLING_INTERVAL);
        } else {
          setError('Délai d\'attente dépassé');
          setLoading(false);
        }
      } catch (err) {
        console.error('Erreur lors de la vérification:', err);
        setError('Une erreur est survenue lors de la vérification du paiement');
        setLoading(false);
      }
    };

    verifyPayment();
  }, [user, navigate, searchParams, pollingCount]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          {loading && (
            <div className="space-y-4">
              <LoadingSpinner size="lg" color="primary" />
              <p className="mt-4 text-gray-600">
                Vérification de votre paiement...
                <br />
                <span className="text-sm text-gray-500">
                  Veuillez patienter, cela peut prendre quelques instants
                </span>
              </p>
            </div>
          )}

          {success && (
            <div className="space-y-4">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Paiement réussi !
              </h1>
              <p className="text-gray-600 mb-6">
                Vos crédits ont été ajoutés à votre compte. Vous allez être redirigé vers votre profil.
              </p>
              <div className="animate-pulse bg-green-100 text-green-800 text-sm rounded-full px-4 py-1 inline-block">
                Redirection en cours...
              </div>
            </div>
          )}

          {error && (
            <div className="space-y-4">
              <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Une erreur est survenue
              </h1>
              <p className="text-gray-600 mb-6">{error}</p>
              <div className="flex flex-col space-y-2">
                <button
                  onClick={() => navigate('/credits/purchase')}
                  className="btn btn-primary"
                >
                  Réessayer
                </button>
                <Link
                  to="/dashboard"
                  className="text-primary hover:underline text-sm mt-2"
                >
                  Retour au tableau de bord
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentConfirmation;